import {
   ChangeDetectionStrategy,
   ChangeDetectorRef,
   Component,
   ComponentFactory,
   ComponentFactoryResolver,
   Input,
   OnDestroy,
   OnInit,
   Type,
   ViewChild,
} from '@angular/core';
import { Store } from '@ngxs/store';
import { Position } from '@progress/kendo-angular-tooltip/dist/es2015/models/position.type';
import _ from 'lodash';
import { iif, Observable, of, Subject } from 'rxjs';
import { distinctUntilChanged, filter, map, switchMap, takeUntil, tap } from 'rxjs/operators';
import { BrickLayoutType } from 'src/app/samwebDescription/samweb/dashboard/brickLayoutType';
import { PilotPanelState } from 'src/app/store/dashboard/pilot-panel/pilot-panel.state';
import { BrickInstancesSelectors } from '../../../store/dashboard/brick-wall/brick-instances/brick-instances.selectors';
import { IBrickDescription } from '../../../store/dashboard/brick-wall/brick-instances/brick-instances.state';
import { BrickSettingsState } from '../../../store/dashboard/brick-wall/brick-settings/brick-settings.state';
import { CommonBrickSettingsSelectors } from '../../../store/dashboard/brick-wall/brick-settings/common-brick-settings/common-brick-settings.selectors';
import { BasicBrickComponent } from '../../bricks/basic-brick-component';
import { BrickRegistry, BrickSpecification } from '../../bricks/brick.registry';
import { ComponentHostDirective } from '../../general-directives/component-host.directive';
import { gridDefaultValue } from '../brick-wall/data/grid-default-values';

@Component({
   selector: 'samweb-brick-container',
   templateUrl: './brick-container.component.html',
   styleUrls: ['./brick-container.component.scss'],
   changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BrickContainerComponent implements OnInit, OnDestroy {
   @ViewChild(ComponentHostDirective, { static: true })
   public brickHost: ComponentHostDirective;

   public isBrickOverlayVisible$: Observable<boolean>;

   public isBrickBeingEdited$: Observable<boolean>;

   public disableBrickOverflow$: Observable<boolean>;

   public brickScaling$: Observable<number>;

   public brickInstanceDescription$: Observable<IBrickDescription>;

   public isBrickSettingsDialogOpened$: Observable<boolean>;

   public dialogPosition$: Observable<Position>;

   @Input()
   public brickInstanceId: string;

   private destroy$ = new Subject();

   constructor(private componentFactoryResolver: ComponentFactoryResolver, private store: Store, private changeDetectorRef: ChangeDetectorRef) {}

   ngOnInit(): void {
      this.isBrickSettingsDialogOpened$ = this.store.select(BrickSettingsState.isBrickSettingDialogOpenForBrickInstance(this.brickInstanceId));

      this.brickScaling$ = this.store.select(CommonBrickSettingsSelectors.getEffectiveBrickScalingFn).pipe(map((fn) => fn(this.brickInstanceId)));

      this.brickInstanceDescription$ = this.store.select(BrickInstancesSelectors.brickDescriptionFn).pipe(map((fn) => fn(this.brickInstanceId)));

      this.brickInstanceDescription$
         .pipe(
            filter((brickInstance) => !_.isNil(brickInstance)),
            distinctUntilChanged(_.isEqual),
            switchMap((brickInstance) =>
               iif(
                  () => brickInstance.brickInstance.brickLayout === BrickLayoutType.PILOT_PANEL,
                  this.store.select(PilotPanelState.isPilotPanelExtended).pipe(
                     distinctUntilChanged(),
                     map((isPilotPanelExtended) => {
                        return { brickInstance, isSmallBrickComponentActive: !isPilotPanelExtended };
                     })
                  ),
                  of({ brickInstance, isSmallBrickComponentActive: false })
               )
            ),
            tap((info) => {
               const brickInstance = info.brickInstance;
               const brickSpecification: BrickSpecification = _(BrickRegistry.brickSpecifications())
                  .filter((x) => x.definition.brickDefinitionId === brickInstance.definition.brickDefinitionId)
                  .head();

               let brickComponent: Type<BasicBrickComponent>;
               if (!_.isNil(brickSpecification.definition.smallBrickComponentAlternative) && info.isSmallBrickComponentActive) {
                  brickComponent = brickSpecification.definition.smallBrickComponentAlternative;
               } else {
                  brickComponent = brickSpecification.component;
               }
               const brickFactory: ComponentFactory<BasicBrickComponent> = this.componentFactoryResolver.resolveComponentFactory(brickComponent);

               const viewContainerRef = this.brickHost.viewContainerRef;
               viewContainerRef.clear();

               const component = viewContainerRef.createComponent<BasicBrickComponent>(brickFactory);
               component.instance.brickInstanceId = brickInstance.brickInstance.brickInstanceId;
               component.instance.brickDefinition = brickInstance.definition;
               component.instance.baseStore = this.store;
               this.changeDetectorRef.markForCheck();
            }),
            takeUntil(this.destroy$)
         )
         .subscribe();

      // We show the popup on the side of the brick having the most space available (without respecting offsets of the gridster instance)
      // This works as long as gridsters minCols equals maxCols (and minRows equals maxRows)
      this.dialogPosition$ = this.brickInstanceDescription$.pipe(
         map((brickDescription) => {
            const isPilotPanel = brickDescription.brickInstance.brickLayout.value === BrickLayoutType.PILOT_PANEL.value;
            const gridster = gridDefaultValue(isPilotPanel);
            const spaceLeftAbove = brickDescription.brickInstance.positionY;
            const spaceLeftBelow = gridster.maxRows - (brickDescription.brickInstance.positionY + brickDescription.brickInstance.rowHeight);
            const spaceLeftLeft = brickDescription.brickInstance.positionX;
            const spaceLeftRight = gridster.maxCols - (brickDescription.brickInstance.positionX + brickDescription.brickInstance.colWidth);

            if (isPilotPanel) {
               if (spaceLeftAbove < 0.25 * gridster.maxRows && spaceLeftBelow < 0.25 * gridster.maxRows) {
                  return 'left';
               }
            }

            const data: { space: number; position: Position }[] = [
               { space: _.isNaN(spaceLeftAbove) ? 0 : spaceLeftAbove, position: 'top' },
               { space: _.isNaN(spaceLeftBelow) ? 0 : spaceLeftBelow, position: 'bottom' },
               { space: _.isNaN(spaceLeftLeft) ? 0 : spaceLeftLeft, position: 'left' },
               { space: _.isNaN(spaceLeftRight) ? 0 : spaceLeftRight, position: 'right' },
            ];
            return _.maxBy(data, 'space').position;
         })
      );

      this.disableBrickOverflow$ = this.brickInstanceDescription$.pipe(
         filter((brickDescription) => !_.isNil(brickDescription)),
         map((brickDescription) => !brickDescription.definition.allowBrickOverflow)
      );

      this.isBrickOverlayVisible$ = this.store.select(BrickSettingsState.isBrickControlPossible).pipe(map((fn) => fn(this.brickInstanceId)));

      this.isBrickBeingEdited$ = this.store.select(BrickSettingsState.isBrickBeingEditedFn).pipe(map((fn) => fn(this.brickInstanceId)));
   }

   ngOnDestroy(): void {
      this.destroy$.next();
      this.destroy$.complete();
   }
}
